/**
 * Browser probe used by tools/self_test.py (Playwright).
 */

import { buildTestMidiBytes } from "@piano-tutor/shared";

export interface LatencySample {
  noteOnWallMs: number;
  signalDetectMs: number | null;
  baseLatencyMs: number;
  outputLatencyMs: number;
}

export interface SuiteResult {
  ok: boolean;
  averages: {
    noteOnWallMs: number;
    signalDetectMs: number;
    outputLatencyMs: number;
    baseLatencyMs: number;
  };
  checks: Record<string, boolean>;
  failures: string[];
  samples: LatencySample[];
  status: Record<string, unknown>;
}

type CoreLike = {
  ensureAudio: () => Promise<void>;
  setAudioMuted: (m: boolean) => Promise<void>;
  setPlayInput: (mode: "midi" | "laptop", opts?: { userChoice?: boolean }) => void;
  startMetronome: () => Promise<void>;
  stopMetronome: () => void;
  getStatus: () => {
    audioReady: boolean;
    audioMuted: boolean;
    playInput: string;
    midiListening: boolean;
    midiDevices: unknown[];
    metronome: { running: boolean };
    activeNotes: number[];
    outputLatencyMs: number;
  };
  synth: {
    audioContext: AudioContext | null;
    masterOutput: GainNode | null;
    noteOn: (n: number, v?: number) => void;
    noteOff: (n: number, v?: number) => void;
    unlockSync: () => void;
    outputLatencySec: number;
  };
};

export class TutorProbe {
  constructor(private core: CoreLike) {}

  async measureNote(midiNote = 60): Promise<LatencySample> {
    await this.core.ensureAudio();
    const ctx = this.core.synth.audioContext;
    const master = this.core.synth.masterOutput;
    if (!ctx || !master) throw new Error("Audio not ready");

    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0;
    master.connect(analyser);

    const data = new Uint8Array(analyser.fftSize);
    const t0 = performance.now();
    this.core.synth.noteOn(midiNote, 120);
    const noteOnWallMs = performance.now() - t0;

    let signalDetectMs: number | null = null;
    const deadline = performance.now() + 80;
    while (performance.now() < deadline) {
      analyser.getByteTimeDomainData(data);
      let peak = 0;
      for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i] - 128));
      if (peak > 6) {
        signalDetectMs = performance.now() - t0;
        break;
      }
      await new Promise((r) => setTimeout(r, 0));
    }

    this.core.synth.noteOff(midiNote);
    try {
      master.disconnect(analyser);
    } catch {
      /* ignore */
    }

    return {
      noteOnWallMs,
      signalDetectMs,
      baseLatencyMs: (ctx.baseLatency ?? 0) * 1000,
      outputLatencyMs: this.core.synth.outputLatencySec * 1000,
    };
  }

  async runSuite(iterations = 10): Promise<SuiteResult> {
    const samples: LatencySample[] = [];
    await this.core.ensureAudio();
    this.core.setPlayInput("laptop", { userChoice: true });

    for (let i = 0; i < iterations; i++) {
      samples.push(await this.measureNote(60 + (i % 4)));
      await new Promise((r) => setTimeout(r, 30));
    }

    const checks: Record<string, boolean> = {};
    await this.core.setAudioMuted(true);
    checks.muteWorks = this.core.getStatus().audioMuted === true;
    await this.core.setAudioMuted(false);
    checks.unmuteWorks = this.core.getStatus().audioMuted === false;

    this.core.setPlayInput("midi", { userChoice: true });
    checks.switchToMidi = this.core.getStatus().playInput === "midi";
    this.core.setPlayInput("laptop", { userChoice: true });
    checks.switchToLaptop = this.core.getStatus().playInput === "laptop";

    await this.core.startMetronome();
    checks.metronomeStarts = this.core.getStatus().metronome.running === true;
    const withMetro = await this.measureNote(64);
    samples.push(withMetro);
    checks.noteOnFastWithMetronome = withMetro.noteOnWallMs < 3;
    this.core.stopMetronome();
    checks.metronomeStops = this.core.getStatus().metronome.running === false;

    // Phase 3 library
    const pieces = (
      this.core as CoreLike & {
        listLibraryPieces: () => Array<{ id: string; notes: unknown[] }>;
        playLibraryPiece: (id: string) => Promise<void>;
        stopLibraryPiece: () => void;
        piecePlayer: { isPlaying: boolean };
      }
    ).listLibraryPieces();
    checks.libraryHasPieces = pieces.length >= 40;
    const firstId = pieces[0]?.id;
    if (firstId) {
      const playPromise = (
        this.core as CoreLike & { playLibraryPiece: (id: string) => Promise<void> }
      ).playLibraryPiece(firstId);
      await new Promise((r) => setTimeout(r, 120));
      checks.libraryPlayStarts = (
        this.core as CoreLike & { piecePlayer: { isPlaying: boolean } }
      ).piecePlayer.isPlaying;
      (this.core as CoreLike & { stopLibraryPiece: () => void }).stopLibraryPiece();
      await playPromise.catch(() => undefined);
      checks.libraryPlayStops = !(
        this.core as CoreLike & { piecePlayer: { isPlaying: boolean } }
      ).piecePlayer.isPlaying;
    } else {
      checks.libraryPlayStarts = false;
      checks.libraryPlayStops = false;
    }

    // Phase 4 MIDI import
    const midiBytes = buildTestMidiBytes();
    const imported = (
      this.core as CoreLike & {
        importMidiFile: (d: Uint8Array, o?: { title?: string }) => { notes: unknown[]; bpm: number; id: string };
        playImportedPiece: () => Promise<void>;
        clearImportedPiece: () => void;
        getImportedPiece: () => { notes: unknown[] } | null;
        piecePlayer: { isPlaying: boolean };
      }
    ).importMidiFile(midiBytes, { title: "SelfTest MIDI" });
    checks.midiImportParses = imported.notes.length >= 4;
    const importPlayPromise = (
      this.core as CoreLike & { playImportedPiece: () => Promise<void> }
    ).playImportedPiece();
    await new Promise((r) => setTimeout(r, 120));
    checks.midiImportPlays = (
      this.core as CoreLike & { piecePlayer: { isPlaying: boolean } }
    ).piecePlayer.isPlaying;
    (this.core as CoreLike & { clearImportedPiece: () => void }).clearImportedPiece();
    await importPlayPromise.catch(() => undefined);
    checks.midiImportClears =
      (this.core as CoreLike & { getImportedPiece: () => unknown }).getImportedPiece() == null;

    // Phase 5 notation
    const coreN = this.core as CoreLike & {
      setNotationMode: (m: string) => void;
      getNotationMode: () => string;
    };
    coreN.setNotationMode("solfege");
    checks.notationSolfege = coreN.getNotationMode() === "solfege";
    coreN.setNotationMode("indian");
    checks.notationIndian = coreN.getNotationMode() === "indian";
    coreN.setNotationMode("finger");
    checks.notationFinger = coreN.getNotationMode() === "finger";
    coreN.setNotationMode("staff");
    checks.notationStaff = coreN.getNotationMode() === "staff";
    coreN.setNotationMode("letter");
    checks.notationLetter = coreN.getNotationMode() === "letter";

    // Phase 6–7 curriculum + exams
    type Phase67Core = Omit<CoreLike, "getStatus"> & {
      listStages: () => Array<{ id: number; classes: Array<{ id: string }> }>;
      previewClassPlan: (id: string, mins: number) => {
        scaleFactor: number;
        segments: Array<{ loops: number; baseLoops: number }>;
      };
      prepareClassSession: (id: string, mins?: number) => unknown;
      startClassSession: () => Promise<void>;
      stopClassSession: () => void;
      getClassSessionStatus: () => {
        phase: string;
        expectedMidis?: number[];
        practiceStats?: { hits: number; remaining: number } | null;
      };
      getCurriculumClass: (id: string) => { defaultMinutes: number; id: string; minMinutes: number } | undefined;
      playNoteImmediate: (n: number, v?: number) => void;
      setProgressState: (p: {
        unlockedStage: number;
        completedClassIds: string[];
        passedExamIds: string[];
      }) => void;
      canTakeExam: (stage: number) => boolean;
      startExamForStage: (stage: number) => void;
      stopExam: () => void;
      getStatus: () => {
        blindMode: boolean;
        exam: { phase: string; expectedMidis: number[]; stats: { hits: number } };
        progress: { unlockedStage: number };
        classSession: { phase: string; expectedMidis?: number[] };
      };
      listExams: () => unknown[];
    };
    const coreC = this.core as unknown as Phase67Core;
    const stages = coreC.listStages();
    checks.curriculumStages = stages.length === 9;
    checks.curriculumClasses =
      stages.reduce((n, s) => n + s.classes.length, 0) >= 80;
    const c11 = coreC.getCurriculumClass("class-1-1");
    checks.curriculumHas11 = !!c11;
    if (c11) {
      const shortPlan = coreC.previewClassPlan(c11.id, c11.defaultMinutes);
      const longPlan = coreC.previewClassPlan(c11.id, c11.defaultMinutes * 2);
      checks.durationScaleUp =
        longPlan.segments[0].loops > shortPlan.segments[0].loops &&
        longPlan.scaleFactor > 1.5;
      coreC.prepareClassSession(c11.id, c11.minMinutes);
      const runPromise = coreC.startClassSession();
      await new Promise((r) => setTimeout(r, 80));
      checks.classSessionRuns = coreC.getClassSessionStatus().phase === "running";

      // Phase 7 — interactive follow-along: feed expected notes
      let fed = 0;
      for (let step = 0; step < 8; step++) {
        const expected = coreC.getClassSessionStatus().expectedMidis ?? [];
        if (!expected.length) break;
        for (const m of expected) {
          coreC.playNoteImmediate(m, 100);
          fed += 1;
        }
        await new Promise((r) => setTimeout(r, 5));
      }
      const afterPractice = coreC.getClassSessionStatus();
      checks.followAlongGrades =
        fed > 0 &&
        ((afterPractice.practiceStats?.hits ?? 0) > 0 ||
          (afterPractice.expectedMidis?.length ?? 0) >= 0) &&
        (afterPractice.practiceStats?.hits ?? 0) >= 1;

      coreC.stopClassSession();
      await runPromise.catch(() => undefined);
      checks.classSessionStops = coreC.getClassSessionStatus().phase === "idle";
    } else {
      checks.durationScaleUp = false;
      checks.classSessionRuns = false;
      checks.classSessionStops = false;
      checks.followAlongGrades = false;
    }

    // Phase 7 — exams unlock stages; classes are optional practice
    coreC.setProgressState({
      unlockedStage: 1,
      completedClassIds: [],
      passedExamIds: [],
    });
    checks.examGateUnlock = coreC.canTakeExam(1) === true;
    checks.examGateLocked = coreC.canTakeExam(2) === false;

    coreC.setProgressState({
      unlockedStage: 1,
      completedClassIds: [],
      passedExamIds: [],
    });
    try {
      coreC.startExamForStage(1);
      const ex = coreC.getStatus();
      checks.examStartsBlind = ex.blindMode === true && ex.exam.phase === "running";
      // Feed a few correct notes while blind
      for (let step = 0; step < 5; step++) {
        const midis = coreC.getStatus().exam.expectedMidis ?? [];
        for (const m of midis) coreC.playNoteImmediate(m, 100);
        await new Promise((r) => setTimeout(r, 5));
      }
      checks.examGradesNotes = coreC.getStatus().exam.stats.hits >= 1;
      coreC.stopExam();
      checks.examStops = coreC.getStatus().exam.phase === "idle";
    } catch {
      checks.examStartsBlind = false;
      checks.examGradesNotes = false;
      checks.examStops = false;
    }
    checks.examDefinitions = (coreC.listExams?.() ?? []).length >= 7;

    const avg = (xs: number[]) =>
      xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 9999;

    const noteOnWallMs = avg(samples.map((s) => s.noteOnWallMs));
    const signalDetectMs = avg(
      samples.map((s) => s.signalDetectMs).filter((x): x is number => x != null),
    );
    const outputLatencyMs = avg(samples.map((s) => s.outputLatencyMs));
    const baseLatencyMs = avg(samples.map((s) => s.baseLatencyMs));

    checks.noteOnHotPathUnder2ms = noteOnWallMs < 2;
    checks.signalInAudioGraphUnder20ms = signalDetectMs < 20;

    const failures = Object.entries(checks)
      .filter(([, v]) => !v)
      .map(([k]) => k);

    return {
      ok: failures.length === 0,
      averages: { noteOnWallMs, signalDetectMs, outputLatencyMs, baseLatencyMs },
      checks,
      failures,
      samples,
      status: { ...this.core.getStatus() },
    };
  }
}
